/**
 * InvoiceNotesPopover. The invoice notes affordance: a notes icon (with a
 * has-notes count badge) that opens the shared centered <NotesModal>
 * (COCKPIT.md section 2.10) as an overlay. Used on both the Invoices grid row
 * and the invoice detail page so the affordance is identical in both places.
 *
 * The modal shows BOTH the invoice's notes (invoice_notes) and its audit trail
 * (the invoice audit feed reading from the unified `audit_log`), matching the
 * Orders modal. Notes + audit load lazily on first open (grid rows do not fire
 * one request per visible row on first paint). The detail page passes `eager`
 * (the invoice read endpoint does not return notes_count and it is a single
 * record) so both load on mount and the badge count derives from the loaded
 * notes. The name is kept for call-site compatibility; it now opens the shared
 * centered modal rather than an anchored popover.
 */

import { useState } from 'react';
import { NotesModal } from '@/components/NotesModal';
import { NotesIconButton } from '@/components/RowActionIcons';
import { formatDateTime } from '@/lib/dateUtils';
import { useAddInvoiceNote, useInvoiceAudit, useInvoiceNotes } from '@/lib/useInvoices';

export function InvoiceNotesPopover({
  documentId,
  title = 'Invoice',
  notesCount,
  canWrite,
  eager = false,
}: {
  documentId: string;
  // Heading shown in the modal (e.g. the invoice number). Defaults to "Invoice".
  title?: string;
  // Known count from the row (grid). On the detail page this is unknown, so pass
  // 0 with `eager` and the count derives from the loaded notes.
  notesCount: number;
  canWrite: boolean;
  // Load the notes on mount instead of on first open (detail page).
  eager?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Defer the notes + audit fetches until the modal is opened at least once
  // (unless eager).
  const [activated, setActivated] = useState(eager);
  const notesQ = useInvoiceNotes(documentId, activated);
  const auditQ = useInvoiceAudit(documentId, activated);
  const addNote = useAddInvoiceNote();

  const notes = (notesQ.data?.data ?? []).map((n) => ({
    id: n.id,
    author: n.created_by,
    body: n.body,
    created_at: n.created_at,
  }));

  const audit = (auditQ.data?.events ?? []).map((e) => ({
    id: e.audit_id,
    actor: e.actor_email,
    action: e.action,
    outcome: e.outcome,
    detail: e.detail,
    occurred_at: e.occurred_at,
  }));

  // Once notes have loaded, prefer their actual length; otherwise use the
  // row-supplied count so the badge shows before any fetch.
  const count = notesQ.data ? notes.length : notesCount;

  return (
    <>
      <NotesIconButton
        count={count}
        onOpen={() => {
          setActivated(true);
          setOpen(true);
        }}
      />
      {open && (
        <NotesModal
          title={title}
          eyebrow="Invoice"
          notes={notes}
          notesLoading={notesQ.isLoading}
          notesError={notesQ.error?.message ?? null}
          audit={audit}
          auditLoading={auditQ.isLoading}
          auditError={auditQ.error?.message ?? null}
          canWrite={canWrite}
          adding={addNote.isPending}
          addError={addNote.error?.message ?? null}
          onAddNote={(body) => addNote.mutate({ documentId, body })}
          formatTimestamp={formatDateTime}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
